import { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { API_BASE, UPLOADS_BASE } from "../constants/api";
import "./D2dChatScreen.css";

const EMPTY_FORM = {
  patient_referral_id: "",
  doctor_id: "",
  chat_datetime: "",
  chat_message: "",
  status: "Active",
  uploads: [],
  existing_upload_paths: [],
  record_video: null,
  record_audio: null,
  existing_record_video_path: "",
  existing_record_audio_path: "",
};

const STATUS_OPTIONS = ["Active", "End Chat", "Block User", "Report Offence"];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromDateTimeLocal = (value) => (value ? `${value.replace("T", " ")}:00` : null);

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

export default function D2dChatScreen() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const loadData = useCallback(async () => {
    try {
      const endpoints = [
        `${API_BASE}/master/d2d-chats`,
        `${API_BASE}/master/patient-referrals`,
        `${API_BASE}/master/doctors`,
      ];
      const responses = await Promise.all(endpoints.map((url) => fetch(url)));
      const failed = responses.find((response) => !response.ok);
      if (failed) throw new Error("Unable to load D2D Chat data.");
      const [chatData, referralData, doctorData] = await Promise.all(
        responses.map((response) => response.json())
      );
      setRows(Array.isArray(chatData) ? chatData : []);
      setReferrals(Array.isArray(referralData) ? referralData : []);
      setDoctors(Array.isArray(doctorData) ? doctorData : []);
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleEdit = (row) => {
    setEditingId(row.chat_session_id);
    setForm({
      patient_referral_id: row.patient_referral_id ? String(row.patient_referral_id) : "",
      doctor_id: row.doctor_id ? String(row.doctor_id) : "",
      chat_datetime: toDateTimeLocal(row.chat_datetime),
      chat_message: row.chat_message || "",
      status: row.status || "Active",
      uploads: [],
      existing_upload_paths: Array.isArray(row.upload_paths) ? row.upload_paths : [],
      record_video: null,
      record_audio: null,
      existing_record_video_path: row.record_video_path || "",
      existing_record_audio_path: row.record_audio_path || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this D2D chat message?")) return;
    try {
      const response = await fetch(`${API_BASE}/master/d2d-chats/${id}`, { method: "DELETE" });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Delete failed.");
      }
      setMessage("Deleted.");
      if (editingId === id) resetForm();
      await loadData();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    }
  };

  const removeExistingUpload = (path) => {
    setForm((prev) => ({
      ...prev,
      existing_upload_paths: prev.existing_upload_paths.filter((item) => item !== path),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.patient_referral_id || !form.doctor_id) {
      setMessage("Patient Referral ID and Doctor ID are required.");
      return;
    }
    if (
      !form.chat_message.trim()
      && !form.uploads.length
      && !form.existing_upload_paths.length
      && !form.record_video
      && !form.existing_record_video_path
      && !form.record_audio
      && !form.existing_record_audio_path
    ) {
      setMessage("Enter a chat message or attach an upload / video / audio.");
      return;
    }

    setSaving(true);
    setMessage("");

    const payload = new FormData();
    payload.append("patient_referral_id", form.patient_referral_id);
    payload.append("doctor_id", form.doctor_id);
    if (form.chat_datetime) {
      payload.append("chat_datetime", fromDateTimeLocal(form.chat_datetime));
    }
    payload.append("chat_message", form.chat_message || "");
    payload.append("status", form.status || "Active");
    payload.append("upload_paths", JSON.stringify(form.existing_upload_paths));
    payload.append("record_video_path", form.existing_record_video_path || "");
    payload.append("record_audio_path", form.existing_record_audio_path || "");
    if (editingId) {
      payload.append("replace_uploads", "true");
    }
    form.uploads.forEach((file) => payload.append("uploads", file));
    if (form.record_video) payload.append("record_video", form.record_video);
    if (form.record_audio) payload.append("record_audio", form.record_audio);

    try {
      const response = await fetch(
        editingId
          ? `${API_BASE}/master/d2d-chats/${editingId}`
          : `${API_BASE}/master/d2d-chats`,
        {
          method: editingId ? "PUT" : "POST",
          body: payload,
        }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || "Save failed.");
      setMessage(editingId ? "Updated." : "Created.");
      resetForm();
      await loadData();
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d2d-chat-master">
      <Form onSubmit={handleSubmit} className="d2d-chat-form">
        <div className="d2d-chat-section">
          <div className="d2d-chat-section-heading">
            <h5>{editingId ? `Edit Chat #${editingId}` : "Create D2D Chat Message"}</h5>
            {editingId && (
              <Button type="button" variant="outline-secondary" size="sm" onClick={resetForm}>
                Cancel Edit
              </Button>
            )}
          </div>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dChatReferral">
              <Form.Label>Patient Referral ID</Form.Label>
              <Form.Select
                required
                value={form.patient_referral_id}
                onChange={(e) => setField("patient_referral_id", e.target.value)}
              >
                <option value="">Select referral</option>
                {referrals.map((referral) => (
                  <option key={referral.referral_id} value={referral.referral_id}>
                    Referral #{referral.referral_id}
                    {referral.patient_name ? ` — ${referral.patient_name}` : ""}
                    {referral.referral_date ? ` — ${referral.referral_date}` : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dChatDoctor">
              <Form.Label>Doctor ID (Referring or Treating)</Form.Label>
              <Form.Select
                required
                value={form.doctor_id}
                onChange={(e) => setField("doctor_id", e.target.value)}
              >
                <option value="">Select doctor</option>
                {doctors.map((doctor) => (
                  <option key={doctor.doctor_id} value={doctor.doctor_id}>
                    {doctor.doctor_id} — {doctor.doctor_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dChatDatetime">
              <Form.Label>Date Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={form.chat_datetime}
                onChange={(e) => setField("chat_datetime", e.target.value)}
              />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dChatStatus">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="d2dChatMessage">
              <Form.Label>Chat Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={form.chat_message}
                onChange={(e) => setField("chat_message", e.target.value)}
                placeholder="Type the chat message"
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="d2dChatUploads">
              <Form.Label>Upload (multiple)</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={(e) =>
                  setField("uploads", Array.from(e.target.files || []))
                }
              />
              {form.uploads.length > 0 && (
                <Form.Text muted>
                  {form.uploads.length} new file{form.uploads.length === 1 ? "" : "s"} selected
                </Form.Text>
              )}
              {form.existing_upload_paths.length > 0 && (
                <div className="d2d-chat-existing-uploads">
                  {form.existing_upload_paths.map((path) => (
                    <div key={path} className="d2d-chat-upload-chip">
                      <a
                        href={`${UPLOADS_BASE}/uploads/${path}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {path}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeExistingUpload(path)}
                        aria-label={`Remove ${path}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="d2dChatRecordVideo">
              <Form.Label>Record Video</Form.Label>
              <Form.Control
                type="file"
                accept="video/*"
                onChange={(e) => setField("record_video", e.target.files?.[0] || null)}
              />
              {form.record_video && (
                <Form.Text muted>New video selected: {form.record_video.name}</Form.Text>
              )}
              {form.existing_record_video_path && !form.record_video && (
                <div className="d2d-chat-existing-uploads">
                  <div className="d2d-chat-upload-chip">
                    <a
                      href={`${UPLOADS_BASE}/uploads/${form.existing_record_video_path}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {form.existing_record_video_path}
                    </a>
                    <button
                      type="button"
                      onClick={() => setField("existing_record_video_path", "")}
                      aria-label="Remove recorded video"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="d2dChatRecordAudio">
              <Form.Label>Record Audio</Form.Label>
              <Form.Control
                type="file"
                accept="audio/*"
                onChange={(e) => setField("record_audio", e.target.files?.[0] || null)}
              />
              {form.record_audio && (
                <Form.Text muted>New audio selected: {form.record_audio.name}</Form.Text>
              )}
              {form.existing_record_audio_path && !form.record_audio && (
                <div className="d2d-chat-existing-uploads">
                  <div className="d2d-chat-upload-chip">
                    <a
                      href={`${UPLOADS_BASE}/uploads/${form.existing_record_audio_path}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {form.existing_record_audio_path}
                    </a>
                    <button
                      type="button"
                      onClick={() => setField("existing_record_audio_path", "")}
                      aria-label="Remove recorded audio"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </Form.Group>
          </Row>

          <div className="d2d-chat-actions">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editingId ? "Update" : "Create"}
            </Button>
            {message && <span className="d2d-chat-message">{message}</span>}
          </div>
        </div>
      </Form>

      <div className="d2d-chat-list">
        <h5>D2D Chat Messages</h5>
        {rows.length === 0 ? (
          <p className="text-muted mb-0">No D2D chat messages yet.</p>
        ) : (
          <div className="table-responsive">
            <Table hover className="d2d-chat-table">
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th>Referral</th>
                  <th>Doctor</th>
                  <th>Date Time</th>
                  <th>Message</th>
                  <th>Uploads</th>
                  <th>Video</th>
                  <th>Audio</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.chat_session_id}>
                    <td>{row.chat_session_id}</td>
                    <td>
                      #{row.patient_referral_id}
                      {row.referral_patient_name ? ` — ${row.referral_patient_name}` : ""}
                    </td>
                    <td>{row.doctor_name || row.doctor_id || "—"}</td>
                    <td>{formatDateTime(row.chat_datetime)}</td>
                    <td className="d2d-chat-preview">
                      {row.chat_message
                        ? row.chat_message.length > 80
                          ? `${row.chat_message.slice(0, 80)}…`
                          : row.chat_message
                        : "—"}
                    </td>
                    <td>
                      {Array.isArray(row.upload_paths) && row.upload_paths.length > 0 ? (
                        <div className="d2d-chat-upload-links">
                          {row.upload_paths.map((path) => (
                            <a
                              key={path}
                              href={`${UPLOADS_BASE}/uploads/${path}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {path}
                            </a>
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {row.record_video_path ? (
                        <a
                          href={`${UPLOADS_BASE}/uploads/${row.record_video_path}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Video
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {row.record_audio_path ? (
                        <a
                          href={`${UPLOADS_BASE}/uploads/${row.record_audio_path}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Audio
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span
                        className={`d2d-chat-status d2d-chat-status--${String(row.status || "")
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {row.status || "—"}
                      </span>
                    </td>
                    <td>
                      <div className="d2d-chat-row-actions">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(row.chat_session_id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
